<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import ImageDetail from './ImageDetail.vue'
import ImageInspector from './ImageInspector.vue'
import ImageSlider from './ImageSlider.vue'
import DefectDetail from './DefectDetail.vue'
// Store
import { useInterfaceStore } from 'Store/pages/imageReview/interfaceStore'

interface PropTypes {
  pushImageRoute: (imageUuid: string) => void
}

const props = defineProps<PropTypes>()

const interfaceStore = useInterfaceStore()
const { isDefectDetailsVisible } = storeToRefs(interfaceStore)

// State for collapsible panels
const showSlider = ref(true)
const showSidebar = ref(true)

// --- Logic to force sidebar open when Defect Details are active ---
const previousSidebarState = ref(showSidebar.value)
watch(isDefectDetailsVisible, (isVisible) => {
  if (isVisible) {
    // 1. Capture user's current preference
    previousSidebarState.value = showSidebar.value
    // 2. Force sidebar open
    showSidebar.value = true
  } else {
    // 3. Restore user's preference
    showSidebar.value = previousSidebarState.value
  }
})
</script>

<template>
  <div class="relative flex h-full max-h-full w-full max-w-full overflow-hidden">
    <div class="relative flex flex-col flex-1 h-full transition-all duration-300 ease-in-out">
      <div class="relative flex-1 bg-black overflow-hidden">
        <ImageInspector />
      </div>

      <div
        class="bg-stone-800 transition-[height] duration-300 ease-in-out overflow-hidden z-10"
        :class="showSlider ? 'h-48' : 'h-12'"
      >
        <ImageSlider
          :push-image-route="props.pushImageRoute"
          :show-slider="showSlider"
          @toggle-slider="showSlider = !showSlider"
        />
      </div>
    </div>

    <div
      class="relative h-full bg-white border-l border-gray-300 transition-[width] duration-300 ease-in-out flex-shrink-0"
      :class="showSidebar ? 'w-1/5 min-w-[300px]' : 'w-0'"
    >
      <button
        class="absolute top-1/2 -left-6 -translate-y-1/2 z-20 bg-white border border-gray-300 border-r-0 text-gray-600 h-12 w-6 flex items-center justify-center rounded-l-md shadow-md hover:bg-gray-50 transition-opacity"
        :class="{ 'opacity-50 cursor-not-allowed': isDefectDetailsVisible }"
        :disabled="isDefectDetailsVisible"
        title="Toggle Sidebar"
        @click="(e) => { showSidebar = !showSidebar; (e.target as HTMLElement).blur() }"
      >
        <span>{{ showSidebar ? '›' : '‹' }}</span>
      </button>

      <div class="w-full h-full overflow-y-auto overflow-x-hidden">
        <template v-if="!isDefectDetailsVisible">
          <ImageDetail />
        </template>
        <template v-else>
          <DefectDetail />
        </template>
      </div>
    </div>
  </div>
</template>
